import { useCallback, useMemo, useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PenLine } from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { useIsDesktopNav } from '@/hooks/useNavBreakpoint';
import { useUnreadPatientMessageCount } from '@/hooks/usePatientMessages';
import { useUnreadUnmatchedEmailCount } from '@/hooks/usePatientEmailThreads';
import { NoAccess } from '@/components/NoAccess';
import { Banner } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { PageLayout } from '@/components/ui/PageLayout';
import { Tabs } from '@/components/ui/Tabs';
import {
  communicationInboxTabFromParam,
  type CommunicationInboxTab,
} from '@/lib/communicationInbox';
import { MessagesInboxPanel } from '@/pages/patients/MessagesInboxPanel';
import { UnmatchedEmailInbox } from '@/pages/patients/UnmatchedEmailInbox';

export function PatientMessagesInbox() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { hasPermission, user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [mobileConversation, setMobileConversation] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [desktopThreadSelected, setDesktopThreadSelected] = useState(false);
  const isDesktop = useIsDesktopNav();
  const onMobileConversationChange = useCallback((open: boolean) => {
    setMobileConversation(open);
  }, []);
  const onDesktopThreadSelectedChange = useCallback((selected: boolean) => {
    setDesktopThreadSelected((current) => (current === selected ? current : selected));
  }, []);

  const canMessages = Boolean(activeClinicId && hasPermission(activeClinicId, 'messages', 'view'));
  const canCreateMessage = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'messages', 'create'),
  );
  const canEmail = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'communication', 'view'),
  );
  const unreadMessages = useUnreadPatientMessageCount(canMessages ? activeClinicId : null);
  const unreadEmails = useUnreadUnmatchedEmailCount(
    canEmail ? activeClinicId : null,
    canEmail ? (user?.id ?? null) : null,
  );

  const requested = communicationInboxTabFromParam(params.get('tab'));
  const activeTab: CommunicationInboxTab = useMemo(() => {
    if (requested === 'email' && canEmail) return 'email';
    if (requested === 'messages' && canMessages) return 'messages';
    if (canMessages) return 'messages';
    return 'email';
  }, [canEmail, canMessages, requested]);

  const tabItems = useMemo(
    () =>
      (
        [
          canMessages
            ? {
                id: 'messages',
                label: t('communicationInbox.tabs.messages', 'Beskeder'),
                badge: unreadMessages.data ?? 0,
              }
            : null,
          canEmail
            ? {
                id: 'email',
                label: t('communicationInbox.tabs.email', 'E-mail'),
                badge: unreadEmails.data ?? 0,
              }
            : null,
        ] as const
      ).filter((item): item is NonNullable<typeof item> => item != null),
    [canEmail, canMessages, t, unreadEmails.data, unreadMessages.data],
  );

  const onTabChange = (id: string) => {
    const next: CommunicationInboxTab = id === 'email' ? 'email' : 'messages';
    setParams(
      (current) => {
        const nextParams = new URLSearchParams(current);
        if (next === 'messages') nextParams.delete('tab');
        else nextParams.set('tab', next);
        if (next !== 'email') nextParams.delete('thread');
        return nextParams;
      },
      { replace: true },
    );
    if (next !== 'messages') setComposeOpen(false);
  };

  if (!activeClinicId) {
    return (
      <PageLayout>
        <Banner tone="error">{t('messages.inbox.no_clinic', 'You have no active clinic.')}</Banner>
      </PageLayout>
    );
  }

  if (!canMessages && !canEmail) {
    return <NoAccess />;
  }

  const showNewMessage = canCreateMessage && activeTab === 'messages' && !mobileConversation;

  return (
    <PageLayout
      fill
      title={mobileConversation ? undefined : t('communicationInbox.title', 'Kommunikation')}
      subtitle={
        mobileConversation
          ? undefined
          : t('communicationInbox.subtitle', 'Patientbeskeder og uidentificerede e-mails')
      }
      actions={
        showNewMessage ? (
          <Button
            variant={isDesktop && desktopThreadSelected ? 'secondary' : 'primary'}
            onClick={() => setComposeOpen(true)}
          >
            <PenLine />
            {t('messages.new_message', 'Ny besked')}
          </Button>
        ) : undefined
      }
    >
      {tabItems.length > 1 && !mobileConversation ? (
        // Page body is mt-s5, and the label sits in the middle of the 44px tab.
        // -mt-s6 leaves the same gap under the title as Aftalebog's week line (mt-s1).
        <Tabs
          className="pointer-events-none -mt-s6 [&_button]:pointer-events-auto"
          items={tabItems}
          value={activeTab}
          onChange={onTabChange}
          overflowLabel={(count) => t('design.more_tabs', { count })}
          ariaLabel={t('communicationInbox.tabs_label', 'Kommunikation-faner')}
        />
      ) : null}
      <div
        className={
          tabItems.length > 1 && !mobileConversation
            ? 'mt-s5 flex min-h-0 flex-1 flex-col'
            : 'flex min-h-0 flex-1 flex-col'
        }
      >
        {activeTab === 'email' ? (
          <UnmatchedEmailInbox
            highlightThreadId={params.get('thread')}
            onMobileConversationChange={onMobileConversationChange}
          />
        ) : (
          <MessagesInboxPanel
            onMobileConversationChange={onMobileConversationChange}
            composeOpen={composeOpen}
            onComposeOpenChange={setComposeOpen}
            onDesktopThreadSelectedChange={onDesktopThreadSelectedChange}
          />
        )}
      </div>
    </PageLayout>
  );
}

export function KommunikationInboxRedirect() {
  const location = useLocation();
  return <Navigate to={`/patients/messages${location.search}`} replace />;
}
